// Expert, part 2: the statistics of trading and keeping the account alive.
// No charts: every question invents its own numbers with rng and is graded
// by the desk's risk math (games/rules.ts), never by luck.
import type { GameDef, Option, Rng } from "../types";
import { LETTERS, price, rText, roundTo, usd } from "../helpers";
import {
  AGAINST_BTC_RISK,
  GOAL_TRADES,
  MAX_COST_R,
  MAX_DAILY_LOSS_R,
  MIN_RR,
  RISK_PCT,
  RSI_BAND,
  VOLUME_MIN,
} from "../rules";

// ── Small shared bits ───────────────────────────────────────────────────────

/** Coins other than BTC (so any of them can be "against BTC"). */
const ALTS = ["ETH", "SOL", "XRP", "DOGE", "ADA", "AVAX", "LINK", "BNB", "SUI"] as const;

/** "1.25%", "0.5%", "12%": a percentage without trailing zeros. */
const pct = (n: number, decimals = 2): string => `${n < 0 ? "−" : ""}${String(roundTo(Math.abs(n), decimals))}%`;

/** "0.55" without trailing zeros. */
const num = (n: number, decimals = 3): string => String(roundTo(n, decimals));

const sum = (xs: number[]): number => xs.reduce((a, b) => a + b, 0);

// ── The math each game grades by (exported for the tests) ───────────────────

/** What one trade makes on average, in R: win rate × average win − loss rate × 1R. */
export const expectancyR = (winPct: number, avgWinR: number, avgLossR = 1): number =>
  (winPct / 100) * avgWinR - (1 - winPct / 100) * avgLossR;

/** The % gain that undoes a loss of `lossPct` %. */
export const recoveryGainPct = (lossPct: number): number => (lossPct / (100 - lossPct)) * 100;

/** Chance (in %) that the next `n` trades all lose at a win rate of `winPct` %. */
export const allLoseChancePct = (winPct: number, n: number): number => (1 - winPct / 100) ** n * 100;

/** Chance (0–1) of at least one run of `n` losses somewhere in `m` trades, each lost with chance `q`. */
export function streakSomewhereChance(q: number, n: number, m: number): number {
  // State = the current run of losses (0 … n−1); reaching n is a hit and stays a hit.
  let dist = new Array<number>(n).fill(0);
  dist[0] = 1;
  let hit = 0;
  for (let t = 0; t < m; t++) {
    const next = new Array<number>(n).fill(0);
    for (let s = 0; s < n; s++) {
      const p = dist[s]!;
      if (p === 0) continue;
      next[0]! += p * (1 - q);
      if (s + 1 >= n) hit += p * q;
      else next[s + 1]! += p * q;
    }
    dist = next;
  }
  return hit;
}

/** % of the account left after `n` losses in a row at `riskPct` % each. */
export const accountLeftPct = (riskPct: number, n: number): number => 100 * (1 - riskPct / 100) ** n;

/** The day's R as the daily guard counts it: each trade's R × its share of the usual risk. */
export const guardDayR = (trades: { r: number; half: boolean }[]): number =>
  sum(trades.map(t => t.r * (t.half ? AGAINST_BTC_RISK : 1)));

// ── 11. Expectancy ──────────────────────────────────────────────────────────

export const expectancy: GameDef = {
  id: "expectancy",
  level: "expert",
  title: "Your edge per trade",
  skill: "Turn a win rate and an average win into what a method makes per trade on average.",
  make(_data, rng) {
    const winPct = rng.int(30, 65);
    const avgWin = roundTo(rng.float(1, 3), 1);
    const e = roundTo(expectancyR(winPct, avgWin), 4);
    // A method that breaks almost exactly even makes a muddy lesson.
    if (Math.abs(e) < 0.03) return null;
    const trades = rng.pick([40, 60, 80, 120, 150]);
    const w = winPct / 100;
    const verdict =
      e > 0
        ? `Positive means the method makes money on average over many trades: about ${rText(e * 100, 0)} per 100 trades.`
        : `Negative means the method slowly loses over many trades, however good the wins feel: about ${rText(e * 100, 0)} per 100 trades.`;
    return {
      kind: "number",
      prompt: `Your journal after ${trades} trades is below. On average, how many R does one trade make? (R = one unit of the risk you planned; type a minus if it loses.)`,
      facts: [
        { label: "Win rate", value: `${winPct}%` },
        { label: "Average win", value: rText(avgWin) },
        { label: "Average loss", value: rText(-1) },
      ],
      answer: e,
      tolerance: 0.02,
      unit: "R",
      decimals: 2,
      explain: `Expectancy = win rate × average win − loss rate × average loss = ${num(w, 2)} × ${avgWin.toFixed(1)}R − ${num(1 - w, 2)} × 1R = ${rText(e, 2)} per trade. ${verdict}`,
    };
  },
};

// ── 12. Drawdown recovery ───────────────────────────────────────────────────

export const drawdownRecovery: GameDef = {
  id: "drawdown-recovery",
  level: "expert",
  title: "Climb back from a loss",
  skill: "See why a big loss needs an even bigger gain to undo it.",
  make(_data, rng) {
    const lossPct = rng.int(5, 60);
    const start = rng.pick([1000, 2000, 2500, 4000, 5000, 10000]);
    const after = roundTo(start * (1 - lossPct / 100), 2);
    const gap = start - after;
    const gain = recoveryGainPct(lossPct);
    return {
      kind: "number",
      prompt: `Your account falls ${lossPct}% from its high. What % gain does it need to get back to where it was?`,
      facts: [
        { label: "Account high", value: usd(start, 0) },
        { label: "Loss", value: `−${lossPct}%` },
        { label: "Now", value: usd(after, 0) },
      ],
      answer: gain,
      tolerance: 0.5,
      unit: "%",
      decimals: 1,
      explain: `Getting from ${usd(after, 0)} back to ${usd(start, 0)} takes ${usd(gap, 0)} more, measured from the smaller balance: ${usd(gap, 0)} ÷ ${usd(after, 0)} = ${gain.toFixed(1)}%. That's why big losses are so hard to recover: −10% needs +11.1%, but −50% needs +100%, so the desk keeps every loss small.`,
    };
  },
};

// ── 13. Losing streak odds ──────────────────────────────────────────────────

export const losingStreakOdds: GameDef = {
  id: "losing-streak-odds",
  level: "expert",
  title: "Losing streak odds",
  skill: "Work out how likely a run of losses is, so a streak doesn't shake you.",
  make(_data, rng) {
    const winPct = rng.int(35, 60);
    // Streaks under 1% would round to almost nothing: keep the lengths that make a real number.
    const lengths = [3, 4, 5, 6, 7, 8].filter(n => allLoseChancePct(winPct, n) >= 1);
    const n = rng.pick(lengths);
    const chance = allLoseChancePct(winPct, n);
    const q = 1 - winPct / 100;
    const in100 = streakSomewhereChance(q, n, 100) * 100;
    const deskCost = 100 - accountLeftPct(RISK_PCT, n);
    return {
      kind: "number",
      prompt: `Your win rate is ${winPct}%. What is the chance (in %) that the next ${n} trades all lose?`,
      facts: [
        { label: "Win rate", value: `${winPct}%` },
        { label: "Losses in a row", value: String(n) },
      ],
      answer: chance,
      tolerance: 0.5,
      unit: "%",
      decimals: 1,
      explain: `Each trade loses with chance ${num(q, 2)} and trades don't remember each other, so multiply: ${num(q, 2)} × ${num(q, 2)} × … (${n} times) = ${chance.toFixed(1)}%. That sounds rare, but over 100 trades the chance of at least one streak this long is about ${Math.round(in100)}%: streaks are normal, so size small enough to survive them (at ${RISK_PCT}% risk, ${n} losses cost about ${pct(deskCost, 1)}).`,
    };
  },
};

// ── 14. Can you trust the record? ───────────────────────────────────────────

export const trustTheRecord: GameDef = {
  id: "trust-the-record",
  level: "expert",
  title: "Can you trust it yet?",
  skill: "Judge a method only after enough trades, and by its average R per trade.",
  make(_data, rng) {
    const kind = rng.pick(["early", "keep", "stop"] as const);
    const n = kind === "early" ? rng.int(5, GOAL_TRADES - 1) : rng.int(GOAL_TRADES, 120);
    const rawAvg =
      kind === "keep"
        ? rng.float(0.06, 0.6)
        : kind === "stop"
          ? rng.float(-0.5, -0.06)
          : rng.chance(0.6)
            ? rng.float(0.2, 0.9)
            : rng.float(-0.7, -0.15);
    const total = roundTo(rawAvg * n, 1);
    const avg = total / n;
    // Rounding the total must not flip the sign or land on a near-zero average.
    if (Math.abs(avg) < 0.04 || Math.sign(avg) !== Math.sign(rawAvg)) return null;
    const enough = n >= GOAL_TRADES;
    const positive = avg > 0;
    const avgLine = `${rText(total)} ÷ ${n} = ${rText(avg, 2)} per trade`;
    const options: Option[] = [
      {
        id: "early",
        label: `Too early: under ${GOAL_TRADES} trades is mostly luck`,
        why: enough ? `${n} trades is at or past the ${GOAL_TRADES}-trade mark: the record is big enough to start judging.` : undefined,
      },
      {
        id: "keep",
        label: "Enough to start judging, and the average is positive: keep the same size",
        why: !enough
          ? `Only ${n} trades: under ${GOAL_TRADES}, a ${positive ? "good" : "bad"} run is often just luck.`
          : !positive
            ? `The average is ${avgLine}: negative, so the method is losing.`
            : undefined,
      },
      {
        id: "stop",
        label: "Enough trades, and the average is negative: stop and review the rules",
        why: !enough
          ? `Only ${n} trades: under ${GOAL_TRADES}, a ${positive ? "good" : "bad"} run is often just luck.`
          : positive
            ? `The average is ${avgLine}: positive, so the rules are working.`
            : undefined,
      },
    ];
    const explain = !enough
      ? `${n} trades is under the ${GOAL_TRADES} the desk wants before judging. The average is ${avgLine}, but a run this short swings on luck: keep the same small size and keep logging.`
      : positive
        ? `The average is ${avgLine}. With ${n} trades (${GOAL_TRADES} or more) and a positive average, the method is earning: keep the same size and keep following the rules.`
        : `The average is ${avgLine}. With ${n} trades (${GOAL_TRADES} or more) and a negative average, the method is losing: stop, and review which rules or setups cost the most.`;
    return {
      kind: "choice",
      prompt: "Here is your practice record so far. What should you do with this method?",
      facts: [
        { label: "Trades taken", value: String(n) },
        { label: "Total result", value: rText(total) },
        { label: "Risk per trade", value: pct(RISK_PCT) },
      ],
      options,
      answer: !enough ? "early" : positive ? "keep" : "stop",
      explain,
    };
  },
};

// ── 15. Correlated risk ─────────────────────────────────────────────────────

export const correlatedRisk: GameDef = {
  id: "correlated-risk",
  level: "expert",
  title: "All coins drop together",
  skill: "Add up the real risk of several open trades, since crypto moves together.",
  make(_data, rng) {
    const account = rng.pick([500, 1000, 2000, 3000, 5000, 10000]);
    const riskPct = rng.pick([RISK_PCT, RISK_PCT, 1]);
    const k = rng.int(2, 4);
    const coins = rng.shuffle(ALTS).slice(0, k);
    const half = coins.map(() => false);
    if (rng.chance(0.5)) for (const i of rng.shuffle(coins.map((_, i) => i)).slice(0, rng.int(1, k - 1))) half[i] = true;
    const per = (account * riskPct) / 100;
    const losses = half.map(h => per * (h ? AGAINST_BTC_RISK : 1));
    const loss = roundTo(sum(losses), 2);
    const lossPct = (loss / account) * 100;
    const anyHalf = half.some(Boolean);
    return {
      kind: "number",
      prompt: `You have ${k} buys open on different coins. Crypto drops together and ${k === 2 ? "both" : `all ${k}`} stops are hit. How much do you lose in total?`,
      facts: [
        { label: "Account", value: usd(account, 0) },
        { label: "Risk per trade", value: `${pct(riskPct)} (full size)` },
        ...coins.map((c, i) => ({ label: c, value: half[i] ? "Buy · against BTC (half risk)" : "Buy · full risk" })),
      ],
      answer: loss,
      tolerance: 0.01,
      unit: "$",
      decimals: 2,
      explain: `A full-size trade risks ${pct(riskPct)} × ${usd(account, 0)} = ${usd(per)}${anyHalf ? `; a half-risk one (against BTC) risks ${usd(per * AGAINST_BTC_RISK)}` : ""}. ${losses.map(l => usd(l)).join(" + ")} = ${usd(loss)}, or ${pct(lossPct)} of the account. Coins move together, so these ${k} trades are really one ${pct(lossPct)} bet, not ${k} small ones.`,
    };
  },
};

// ── 16. The leverage myth ───────────────────────────────────────────────────

const PRICED = [
  { coin: "BTC", px: 64000, qtyDec: 3 },
  { coin: "ETH", px: 3200, qtyDec: 2 },
  { coin: "SOL", px: 145, qtyDec: 1 },
  { coin: "LINK", px: 18, qtyDec: 0 },
  { coin: "XRP", px: 2.4, qtyDec: 0 },
  { coin: "DOGE", px: 0.35, qtyDec: 0 },
] as const;

/** A price rounded to what price() shows. */
const shownPrice = (x: number): number => Number(price(x).replace(/,/g, ""));

function leverageChoice(rng: Rng) {
  const c = rng.pick(PRICED);
  const isBuy = rng.chance(0.5);
  // Prices exactly as shown, so the player can redo the math from the facts.
  const entry = shownPrice(c.px * rng.float(0.9, 1.1));
  const stopPct = roundTo(rng.float(0.8, 2.5), 1);
  const stop = shownPrice(entry * (1 + (isBuy ? -1 : 1) * (stopPct / 100)));
  const posTarget = rng.pick([500, 1000, 2000, 3000, 5000]);
  const qty = roundTo(posTarget / entry, c.qtyDec);
  if (qty <= 0) return null;
  const dist = Math.abs(entry - stop);
  const loss = qty * dist;
  const pos = qty * entry;
  const l1 = rng.pick([2, 3, 5]);
  // Keep the liquidation price well past the stop at the higher leverage (it sits about 100 ÷ leverage % away).
  const l2s = [10, 20, 25, 50].filter(l => 100 / l >= 2.5 * stopPct);
  if (l2s.length === 0) return null;
  const l2 = rng.pick(l2s);
  const side = isBuy ? "buy" : "sell";
  const qtyText = qty.toLocaleString("en-US", { maximumFractionDigits: c.qtyDec });
  const options: Option[] = rng.shuffle([
    { id: "same", label: `The same ${usd(loss)} both times` },
    {
      id: "more",
      label: `${usd(loss)} at ${l1}×, but ${usd((loss * l2) / l1)} at ${l2}×`,
      why: "Leverage doesn't change how many coins you hold or where the stop is, so it can't make the stop-out bigger. Only a bigger position would.",
    },
    {
      id: "less",
      label: `${usd(loss)} at ${l1}×, but only ${usd((loss * l1) / l2)} at ${l2}×`,
      why: "Higher leverage only locks less of your cash as margin. The coins and the stop are the same, so the loss is the same.",
    },
  ]);
  return {
    kind: "choice" as const,
    prompt: `You ${side} the same ${qtyText} ${c.coin} with the same stop, once at ${l1}× leverage and once at ${l2}×. How much do you lose if the stop is hit?`,
    facts: [
      { label: "Side", value: isBuy ? "Buy" : "Sell" },
      { label: "Entry", value: price(entry) },
      { label: "Stop", value: price(stop) },
      { label: "Size", value: `${qtyText} ${c.coin} (${usd(pos)})` },
      { label: "Liquidation", value: "Far past the stop at both" },
    ],
    options,
    answer: "same",
    explain: `Loss at the stop = coins × distance to the stop = ${qtyText} × ${price(dist)} = ${usd(loss)}, at any leverage. Leverage only changes the margin locked (${usd(pos / l1)} at ${l1}× vs ${usd(pos / l2)} at ${l2}×) and how close liquidation is: risk is set by size and stop distance.`,
  };
}

function leverageMargin(rng: Rng) {
  const coin = rng.pick(["BTC", ...ALTS]);
  const pos = rng.pick([500, 1000, 1500, 2000, 2500, 3000, 4000, 5000, 6000, 8000]);
  const lev = rng.pick([2, 3, 4, 5, 10, 20, 25]);
  const stopPct = roundTo(lev >= 20 ? rng.float(0.8, 1.5) : rng.float(0.8, 2.5), 1);
  const margin = pos / lev;
  const lossAtStop = (pos * stopPct) / 100;
  return {
    kind: "number" as const,
    prompt: `You open a ${usd(pos, 0)} ${coin} position at ${lev}× leverage. How much of your cash is locked as margin?`,
    facts: [
      { label: "Position size", value: usd(pos, 0) },
      { label: "Leverage", value: `${lev}×` },
      { label: "Stop distance", value: pct(stopPct, 1) },
    ],
    answer: margin,
    tolerance: Math.max(0.01, margin * 0.01),
    unit: "$" as const,
    decimals: 2,
    explain: `Margin = position ÷ leverage = ${usd(pos, 0)} ÷ ${lev} = ${usd(margin)}. The risk doesn't change with it: a stop ${pct(stopPct, 1)} away still loses ${usd(lossAtStop)} at any leverage, because leverage only changes how much cash is locked (and how close liquidation is).`,
  };
}

export const leverageMyth: GameDef = {
  id: "leverage-myth",
  level: "expert",
  title: "Does leverage change risk?",
  skill: "See that size and stop distance set the risk; leverage only sets the margin.",
  make(_data, rng) {
    return rng.chance(0.5) ? leverageChoice(rng) : leverageMargin(rng);
  },
};

// ── 17. Ruin by size ────────────────────────────────────────────────────────

export const ruinBySize: GameDef = {
  id: "ruin-by-size",
  level: "expert",
  title: "Survive a losing streak",
  skill: "See how the risk per trade decides what a losing streak leaves of the account.",
  make(_data, rng) {
    const riskPct = rng.pick([0.5, 1, 2, 5, 10]);
    const n = rng.int(5, 15);
    const left = accountLeftPct(riskPct, n);
    const keep = num(1 - riskPct / 100, 3);
    const compare =
      riskPct === RISK_PCT
        ? `That's the desk's own ${pct(RISK_PCT)}: at 5% the same streak would leave only ${accountLeftPct(5, n).toFixed(1)}%.`
        : `At the desk's ${pct(RISK_PCT)} the same streak leaves ${accountLeftPct(RISK_PCT, n).toFixed(1)}%.`;
    return {
      kind: "number",
      prompt: `You risk ${pct(riskPct)} of the account on every trade and lose ${n} trades in a row. What % of the account is left?`,
      facts: [
        { label: "Risk per trade", value: pct(riskPct) },
        { label: "Losses in a row", value: String(n) },
      ],
      answer: left,
      tolerance: 0.5,
      unit: "%",
      decimals: 1,
      explain: `Each loss takes ${pct(riskPct)} of what is left, so multiply: ${keep} × ${keep} × … (${n} times) = ${left.toFixed(1)}% left. ${compare} Small risk per trade is what lets you survive the streaks every method has.`,
    };
  },
};

// ── 18. Today's weighted R ──────────────────────────────────────────────────

const DAY_RS = [-1, -1, -1, -0.5, 0, 0.4, 0.8, 1.5, 1.5, 2.5] as const;

export const weightedDay: GameDef = {
  id: "weighted-day",
  level: "expert",
  title: "Today's R, half-risk counted",
  skill: "Count the day's R the way the daily guard does: half-risk trades count half.",
  make(_data, rng) {
    for (let attempt = 0; attempt < 20; attempt++) {
      const k = rng.int(2, 4);
      const trades = Array.from({ length: k }, () => ({ r: rng.pick(DAY_RS) as number, half: rng.chance(0.45) }));
      if (!trades.some(t => t.half)) trades[rng.int(0, k - 1)]!.half = true;
      if (trades.every(t => t.half)) trades[rng.int(0, k - 1)]!.half = false;
      // The weighting must matter: some half-risk trade has to win or lose something.
      if (!trades.some(t => t.half && t.r !== 0)) continue;
      const day = roundTo(guardDayR(trades), 4);
      // "1.5R − ½ × 1.0R + 0.4R": signs between the terms, sizes inside them.
      const expr = trades
        .map((t, i) => {
          const size = `${t.half ? "½ × " : ""}${Math.abs(t.r).toFixed(1)}R`;
          return i === 0 ? `${t.r < 0 ? "−" : ""}${size}` : `${t.r < 0 ? "−" : "+"} ${size}`;
        })
        .join(" ");
      return {
        kind: "number",
        prompt: "Here are today's closed trades. How many R does the daily guard count for the day? (R = one unit of each trade's own planned risk.)",
        facts: trades.map((t, i) => ({
          label: `Trade ${i + 1}`,
          value: `${rText(t.r)} · ${t.half ? "against BTC (half risk)" : "full risk"}`,
        })),
        answer: day,
        tolerance: 0.02,
        unit: "R",
        decimals: 2,
        explain: `Trades against BTC were sized at half the usual risk, so their R counts half: ${expr} = ${rText(day, 2)}. The −${MAX_DAILY_LOSS_R}R day limit is in full-size R, so a half-size loss uses up only half of it, matching the dollars really lost.`,
      };
    }
    return null;
  },
};

// ── 19. Journal detective ───────────────────────────────────────────────────

const JOURNAL_DIMS = [
  {
    prompt: (n: number) => `Your last ${n} trades, tagged by setup. Which setup loses the most per trade on average?`,
    tags: [
      { id: "trend", label: "Trend pullback" },
      { id: "range", label: "Range fade" },
      { id: "breakout", label: "Breakout retest" },
    ],
  },
  {
    prompt: (n: number) => `Your last ${n} trades, tagged by the session they were taken in (UTC). Which session loses the most per trade on average?`,
    tags: [
      { id: "asia", label: "Asia" },
      { id: "london", label: "London" },
      { id: "ny", label: "New York" },
    ],
  },
  {
    prompt: (n: number) => `Your last ${n} trades, tagged by whether they went against BTC's strong trend. Which group loses the most per trade on average?`,
    tags: [
      { id: "against", label: "Against BTC" },
      { id: "with", label: "With BTC" },
    ],
  },
] as const;

const LEAK_RS = [-1, -1, -1, -1, 0, 0.5, 1.5] as const;
const OTHER_RS = [-1, -1, 0, 0.5, 1.5, 1.5, 2.5] as const;

export const journalDetective: GameDef = {
  id: "journal-detective",
  level: "expert",
  title: "Find the leak",
  skill: "Find the weakest part of your trading by average R per tag in your journal.",
  make(_data, rng) {
    const dim = rng.pick(JOURNAL_DIMS);
    const tags = dim.tags as readonly { id: string; label: string }[];
    for (let attempt = 0; attempt < 40; attempt++) {
      const n = rng.int(8, 12);
      const leak = rng.int(0, tags.length - 1);
      // Every tag gets at least two trades so its average means something.
      const tagOf = [...tags.flatMap((_, i) => [i, i]), ...Array.from({ length: n - 2 * tags.length }, () => rng.int(0, tags.length - 1))];
      const trades = rng.shuffle(tagOf).map(t => ({ tag: t, r: (t === leak ? rng.pick(LEAK_RS) : rng.pick(OTHER_RS)) as number }));
      const stats = tags.map((_, i) => {
        const rs = trades.filter(t => t.tag === i).map(t => t.r);
        return { count: rs.length, avg: sum(rs) / rs.length };
      });
      const lowest = stats.reduce((best, s, i) => (s.avg < stats[best]!.avg ? i : best), 0);
      const clear = stats.every((s, i) => i === lowest || s.avg - stats[lowest]!.avg >= 0.3);
      if (!clear) continue;
      const avgText = (i: number) => `${tags[i]!.label} ${rText(stats[i]!.avg, 2)} (${stats[i]!.count} trades)`;
      const options: Option[] = rng.shuffle(tags.map((t, i) => ({
        id: t.id,
        label: t.label,
        why: i === lowest ? undefined : `${t.label} averages ${rText(stats[i]!.avg, 2)} per trade over ${stats[i]!.count} trades: better than ${tags[lowest]!.label} (${rText(stats[lowest]!.avg, 2)}).`,
      })));
      return {
        kind: "choice",
        prompt: dim.prompt(n),
        facts: trades.map((t, i) => ({ label: `Trade ${i + 1}`, value: `${tags[t.tag]!.label} · ${rText(t.r)}` })),
        options,
        answer: tags[lowest]!.id,
        explain: `Average R per trade: ${tags.map((_, i) => avgText(i)).join(", ")}. ${tags[lowest]!.label} is the lowest by at least 0.3R, so it's the leak to fix or skip first. Compare averages, not totals, so a tag isn't blamed just for having more trades.`,
      };
    }
    return null;
  },
};

// ── 20. Best of four ────────────────────────────────────────────────────────

type CheckRule = "bias" | "trend" | "rr" | "cost" | "volume" | "rsi";
const CHECK_RULES: CheckRule[] = ["bias", "trend", "rr", "cost", "volume", "rsi"];

interface Candidate {
  letter: string;
  coin: string;
  buy: boolean;
  bias: "bullish" | "bearish";
  trend: "up" | "down";
  rr: number;
  cost: number;
  volume: number;
  rsi: number;
  fails: CheckRule | null;
}

const rsiBand = (buy: boolean) => RSI_BAND[buy ? "LONG" : "SHORT"].b;

function makeCandidate(rng: Rng, letter: string, coin: string, fails: CheckRule | null): Candidate {
  const buy = rng.chance(0.5);
  const [lo, hi] = rsiBand(buy);
  const agreeBias = buy ? "bullish" : "bearish";
  const agreeTrend = buy ? "up" : "down";
  return {
    letter,
    coin,
    buy,
    bias: fails === "bias" ? (buy ? "bearish" : "bullish") : agreeBias,
    trend: fails === "trend" ? (buy ? "down" : "up") : agreeTrend,
    // Passing and failing values sit well clear of each rule's line.
    rr: fails === "rr" ? roundTo(rng.float(0.8, 1.3), 1) : roundTo(rng.float(1.7, 3), 1),
    cost: fails === "cost" ? roundTo(rng.float(0.32, 0.6), 2) : roundTo(rng.float(0.05, 0.2), 2),
    volume: fails === "volume" ? roundTo(rng.float(0.2, 0.4), 1) : roundTo(rng.float(0.7, 2.2), 1),
    rsi: fails === "rsi" ? (rng.chance(0.5) ? rng.int(hi + 6, hi + 15) : rng.int(lo - 12, lo - 5)) : rng.int(lo + 8, hi - 7),
    fails,
  };
}

const sideWord = (c: Candidate) => (c.buy ? "buy" : "sell");

function failWhy(c: Candidate): string {
  const [lo, hi] = rsiBand(c.buy);
  switch (c.fails) {
    case "bias":
      return `${c.letter} breaks the 4h bias rule: the 4h is ${c.bias}, but it's a ${sideWord(c)}.`;
    case "trend":
      return `${c.letter} breaks the 1h trend rule: EMA 20 is ${c.trend === "up" ? "above" : "below"} EMA 50 (1h ${c.trend}), but it's a ${sideWord(c)}.`;
    case "rr":
      return `${c.letter} breaks the reward rule: ${c.rr.toFixed(1)}× the risk is under the ${MIN_RR}× minimum.`;
    case "cost":
      return `${c.letter} breaks the cost rule: ${c.cost.toFixed(2)}R of fees and slippage is over the ${MAX_COST_R}R limit.`;
    case "volume":
      return `${c.letter} breaks the volume rule: ${c.volume.toFixed(1)}× the average is under ${VOLUME_MIN}×, too quiet to count.`;
    case "rsi":
      return `${c.letter} breaks the RSI rule: ${c.rsi} is outside the ${sideWord(c)} band of ${lo}–${hi}.`;
    default:
      return "";
  }
}

function failShort(c: Candidate): string {
  switch (c.fails) {
    case "bias": return `4h ${c.bias}`;
    case "trend": return `1h ${c.trend}`;
    case "rr": return `reward ${c.rr.toFixed(1)}×`;
    case "cost": return `cost ${c.cost.toFixed(2)}R`;
    case "volume": return `volume ${c.volume.toFixed(1)}×`;
    case "rsi": return `RSI ${c.rsi}`;
    default: return "";
  }
}

export const bestOfFour: GameDef = {
  id: "best-of-four",
  level: "expert",
  title: "Which setup passes?",
  skill: "Check a trade idea against every desk rule at once and keep only the one that passes all.",
  make(_data, rng) {
    const fails = rng.shuffle(CHECK_RULES).slice(0, 3);
    const winnerAt = rng.int(0, 3);
    const failing = [...fails];
    const coins = rng.shuffle(["BTC", ...ALTS]).slice(0, 4);
    const cands = LETTERS.slice(0, 4).map((letter, i) => makeCandidate(rng, letter, coins[i]!, i === winnerAt ? null : failing.shift()!));
    const win = cands[winnerAt]!;
    const [lo, hi] = rsiBand(win.buy);
    const others = cands.filter(c => c !== win);
    return {
      kind: "choice",
      prompt: "Four trade ideas. Which one passes every desk rule? (1h up = EMA 20 above EMA 50; cost is in R, one unit of the risk.)",
      facts: cands.map(c => ({
        label: `${c.letter} · ${c.coin}`,
        value: `${c.buy ? "Buy" : "Sell"} · 4h ${c.bias} · 1h ${c.trend} · reward ${c.rr.toFixed(1)}× risk · cost ${c.cost.toFixed(2)}R · vol ${c.volume.toFixed(1)}× · RSI ${c.rsi}`,
      })),
      options: cands.map(c => ({
        id: c.letter,
        label: `${c.letter} · ${c.coin} ${sideWord(c)}`,
        why: c.fails ? failWhy(c) : undefined,
      })),
      answer: win.letter,
      explain: `${win.letter} passes all six: the 4h bias and 1h trend agree with the ${sideWord(win)}, reward ${win.rr.toFixed(1)}× ≥ ${MIN_RR}×, cost ${win.cost.toFixed(2)}R ≤ ${MAX_COST_R}R, volume ${win.volume.toFixed(1)}× ≥ ${VOLUME_MIN}×, and RSI ${win.rsi} is inside ${lo}–${hi}. The others each break one rule: ${others.map(c => `${c.letter} (${failShort(c)})`).join(", ")}.`,
    };
  },
};

export const GAMES: GameDef[] = [
  expectancy,
  drawdownRecovery,
  losingStreakOdds,
  trustTheRecord,
  correlatedRisk,
  leverageMyth,
  ruinBySize,
  weightedDay,
  journalDetective,
  bestOfFour,
];
